import { apiUrl } from '../config/api';

export class ApiError extends Error {
	readonly status: number;

	constructor(message: string, status: number) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
	}
}

export const apiErrorMessage = (error: unknown): string => {
	if (error instanceof ApiError) return error.message;
	if (error instanceof TypeError) return 'No se pudo conectar con el servidor.';
	if (error instanceof Error && error.message) return error.message;
	return 'No se pudo completar la acción.';
};

/** Called when a signed-in request comes back without a session. */
let onUnauthorized: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: (() => void) | null): void => {
	onUnauthorized = handler;
};

export const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
	const headers = new Headers(init?.headers);
	const isForm = typeof FormData !== 'undefined' && init?.body instanceof FormData;
	if (init?.body !== undefined && !isForm && !headers.has('Content-Type')) {
		headers.set('Content-Type', 'application/json');
	}

	const response = await fetch(apiUrl(path), {
		...init,
		credentials: 'include',
		headers,
	});

	if (response.status === 204) return undefined as T;

	const body = (await response.json().catch(() => null)) as { message?: string | string[] } | T | null;

	if (!response.ok) {
		if (response.status === 401) onUnauthorized?.();
		const raw = body && typeof body === 'object' && 'message' in body ? body.message : undefined;
		const message = Array.isArray(raw) ? raw[0] : raw;
		throw new ApiError(
			typeof message === 'string' && message.length > 0 ? message : 'No se pudo completar la acción.',
			response.status
		);
	}

	return body as T;
};

export const queryString = (params: Record<string, string | number | undefined>): string => {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== '') search.set(key, String(value));
	}
	const text = search.toString();
	return text ? `?${text}` : '';
};

/** Inclusive local calendar days for the review and audit range filters. */
export const dateRangeParams = (range: string): { from: string; to: string } => {
	const days = range === 'últimos-7-días' ? 7 : range === 'últimos-90-días' ? 90 : 30;
	const to = new Date();
	const from = new Date();
	from.setDate(to.getDate() - (days - 1));
	return { from: calendarDay(from), to: calendarDay(to) };
};

const calendarDay = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};
